// Offline capture queue substrate (P0-UP-02 / CAP-0.x).
//
// An encrypted-at-rest, append-only / conflict-free queue that the Phase-1 audio recorder will
// build on. This module is ONLY the substrate — there is no audio recorder here; it stores opaque
// capture items + their (optionally large) binary payloads and drains them when the device is online.
//
// Storage layout:
//  - The INDEX (lightweight metadata per item) is persisted as a single encrypted JSON document via
//    @capacitor/preferences on native / localStorage on web (see localConfidentialStore).
//  - Large BLOBS (e.g. recorded audio) are persisted via @capacitor/filesystem on native (base64 in
//    the app-sandboxed Data dir) and via an encrypted Preferences/localStorage entry on web (where
//    there is no native filesystem). Blobs are encrypted at rest by the same confidential store used
//    for the index, so nothing confidential is written in the clear.
//
// Conflict-free design: every mutation is an append/overwrite of a single item keyed by its UUID.
// `enqueue` only ever adds; `updateState` performs a state transition + shallow patch on one item;
// there is no cross-item invariant to violate, so concurrent writers converge.
//
// HARD RULE: every native call is guarded by isNative() so the website build is unaffected — on web
// this degrades to localStorage (index) + encrypted localStorage (blobs) with the same API surface.

import { isNative } from './platform';
import {
  getConfidential,
  setConfidential,
  removeConfidential,
} from '@/lib/localConfidentialStore';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * Item lifecycle. Append-only forward progression in the happy path; `failed` is terminal-until-retry
 * (a retry transitions it back to `pending_upload`).
 *
 *   recording -> pending_upload -> uploading -> finalizing -> done
 *                      ^                                         |
 *                      └──────────────── (retry) ──── failed ◄──┘
 */
export type QueueItemState =
  | 'recording'
  | 'pending_upload'
  | 'uploading'
  | 'finalizing'
  | 'done'
  | 'failed';

export interface QueueItem {
  /** Stable client-generated id; also the idempotency key (`capture_uuid`) for the server insert. */
  id: string;
  state: QueueItemState;
  /** epoch ms — set on enqueue, never mutated. */
  createdAt: number;
  /** epoch ms — bumped on every state change. */
  updatedAt: number;
  /** MIME of the payload blob, if any (e.g. 'audio/m4a'). */
  contentType?: string;
  /** Byte size of the payload, if known. */
  size?: number;
  /** Storage object path the blob should be uploaded to (filled once known). */
  objectPath?: string;
  /** Server `research_sources.id` once `research_create_mobile_source` has run. */
  sourceId?: string;
  /** TUS upload URL captured after creation so the upload can resume on `Upload-Offset`. */
  uploadUrl?: string;
  /** Failure count — drives backoff / give-up policy in the recorder layer. */
  attempts?: number;
  /** Last error message (for the retry UI). */
  lastError?: string;
  /** Free-form bag for recorder-specific metadata (consent basis, duration, …). */
  meta?: Record<string, unknown>;
  /** True when a payload blob has been persisted for this item. */
  hasBlob?: boolean;
}

/** Fields a caller may supply when enqueuing. `id` is generated if omitted. */
export interface EnqueueInput {
  id?: string;
  state?: QueueItemState;
  contentType?: string;
  objectPath?: string;
  sourceId?: string;
  meta?: Record<string, unknown>;
  /** Optional payload to persist alongside the item (encrypted at rest). */
  blob?: Blob;
}

/**
 * Handler invoked once per drainable item. Resolve to advance/clear the item yourself (via
 * updateState/remove), or throw to mark it `failed` (attempts incremented). The blob, if present,
 * is provided so the handler can upload it without re-reading storage.
 */
export type DrainHandler = (item: QueueItem, blob: Blob | null) => Promise<void>;

// ---------------------------------------------------------------------------
// Keys
// ---------------------------------------------------------------------------

const INDEX_KEY = 'capture_queue_index_v1';
const blobKey = (id: string) => `capture_queue_blob_${id}`;
// Native blob files live under the app-sandboxed Data directory.
const BLOB_DIR = 'capture-queue';
const blobFile = (id: string) => `${BLOB_DIR}/${id}.b64`;

// States that `drainPending` will hand to the handler.
const DRAINABLE: ReadonlySet<QueueItemState> = new Set<QueueItemState>([
  'pending_upload',
  'uploading',
  'finalizing',
]);

// ---------------------------------------------------------------------------
// Index persistence (encrypted)
// ---------------------------------------------------------------------------

async function readIndex(): Promise<QueueItem[]> {
  const raw = await getConfidential(INDEX_KEY);
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as QueueItem[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

async function writeIndex(items: QueueItem[]): Promise<void> {
  // No TTL on the queue index — captures must survive until uploaded; pass a very large ttl.
  await setConfidential(INDEX_KEY, JSON.stringify(items), Number.MAX_SAFE_INTEGER);
}

// ---------------------------------------------------------------------------
// Blob persistence (encrypted on web; encrypted base64 in the native sandbox)
// ---------------------------------------------------------------------------

async function blobToBase64(blob: Blob): Promise<string> {
  const buf = await blob.arrayBuffer();
  const bytes = new Uint8Array(buf);
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

function base64ToBlob(b64: string, contentType?: string): Blob {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], contentType ? { type: contentType } : undefined);
}

async function writeBlob(id: string, blob: Blob): Promise<void> {
  const b64 = await blobToBase64(blob);
  if (isNative()) {
    // Native: store the raw base64 in the app-sandboxed Data dir via @capacitor/filesystem.
    // NOTE (build-host hardening): the Data dir is app-sandboxed but not hardware-encrypted; for
    // confidential audio the file should additionally be wrapped by the platform Data Protection
    // class (iOS NSFileProtectionComplete) / encrypted-file (Android EncryptedFile) at native layer.
    const { Filesystem, Directory } = await import('@capacitor/filesystem');
    await Filesystem.mkdir({
      path: BLOB_DIR,
      directory: Directory.Data,
      recursive: true,
    }).catch(() => {
      /* already exists */
    });
    await Filesystem.writeFile({
      path: blobFile(id),
      data: b64,
      directory: Directory.Data,
    });
  } else {
    // Web: encrypt the base64 payload at rest (no native filesystem available).
    await setConfidential(blobKey(id), b64, Number.MAX_SAFE_INTEGER);
  }
}

async function readBlob(id: string, contentType?: string): Promise<Blob | null> {
  if (isNative()) {
    try {
      const { Filesystem, Directory } = await import('@capacitor/filesystem');
      const { data } = await Filesystem.readFile({
        path: blobFile(id),
        directory: Directory.Data,
      });
      const b64 = typeof data === 'string' ? data : await blobToBase64(data as Blob);
      return base64ToBlob(b64, contentType);
    } catch {
      return null;
    }
  }
  const b64 = await getConfidential(blobKey(id));
  return b64 ? base64ToBlob(b64, contentType) : null;
}

async function deleteBlob(id: string): Promise<void> {
  if (isNative()) {
    try {
      const { Filesystem, Directory } = await import('@capacitor/filesystem');
      await Filesystem.deleteFile({ path: blobFile(id), directory: Directory.Data });
    } catch {
      /* already gone */
    }
    return;
  }
  await removeConfidential(blobKey(id));
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

function genId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `cap_${Date.now()}_${Math.random().toString(36).slice(2)}`;
}

/** Append a new item (and persist its blob, if supplied). Returns the stored item. */
export async function enqueue(input: EnqueueInput): Promise<QueueItem> {
  const now = Date.now();
  const id = input.id ?? genId();

  if (input.blob) {
    await writeBlob(id, input.blob);
  }

  const item: QueueItem = {
    id,
    state: input.state ?? 'pending_upload',
    createdAt: now,
    updatedAt: now,
    contentType: input.contentType ?? input.blob?.type,
    size: input.blob?.size,
    objectPath: input.objectPath,
    sourceId: input.sourceId,
    attempts: 0,
    meta: input.meta,
    hasBlob: !!input.blob,
  };

  const items = await readIndex();
  // Idempotent: if the id already exists, keep the existing item (append-only / conflict-free).
  if (items.some((i) => i.id === id)) return items.find((i) => i.id === id)!;
  items.push(item);
  await writeIndex(items);
  return item;
}

/** All queued items, newest last (insertion order). */
export async function list(): Promise<QueueItem[]> {
  return readIndex();
}

/** One item by id, or null. */
export async function getById(id: string): Promise<QueueItem | null> {
  const items = await readIndex();
  return items.find((i) => i.id === id) ?? null;
}

/**
 * Transition an item to `state` and shallow-merge an optional patch. No-op if the id is unknown.
 * `attempts` is auto-incremented when moving into `failed`. Returns the updated item (or null).
 */
export async function updateState(
  id: string,
  state: QueueItemState,
  patch?: Partial<Omit<QueueItem, 'id' | 'createdAt'>>,
): Promise<QueueItem | null> {
  const items = await readIndex();
  const idx = items.findIndex((i) => i.id === id);
  if (idx === -1) return null;

  const prev = items[idx];
  const next: QueueItem = {
    ...prev,
    ...patch,
    id: prev.id,
    createdAt: prev.createdAt,
    state,
    updatedAt: Date.now(),
    attempts: state === 'failed' ? (prev.attempts ?? 0) + 1 : patch?.attempts ?? prev.attempts,
  };
  items[idx] = next;
  await writeIndex(items);
  return next;
}

/** Remove an item and its blob entirely. */
export async function remove(id: string): Promise<void> {
  const items = await readIndex();
  const next = items.filter((i) => i.id !== id);
  if (next.length !== items.length) await writeIndex(next);
  await deleteBlob(id);
}

/**
 * Reachability-aware drain. Only runs when the device reports online (via @capacitor/network on
 * native; navigator.onLine on web). Walks every drainable item (pending_upload / uploading /
 * finalizing) oldest-first and invokes `handler`. The handler owns the state transition on success;
 * a thrown handler marks the item `failed` (attempts++). Returns the count handed to the handler.
 */
export async function drainPending(handler: DrainHandler): Promise<number> {
  if (!(await isOnline())) return 0;

  const items = await readIndex();
  const pending = items
    .filter((i) => DRAINABLE.has(i.state))
    .sort((a, b) => a.createdAt - b.createdAt);

  let processed = 0;
  for (const item of pending) {
    // Re-check connectivity between items so a mid-drain disconnect stops cleanly.
    if (!(await isOnline())) break;
    const blob = item.hasBlob ? await readBlob(item.id, item.contentType) : null;
    try {
      await handler(item, blob);
      processed += 1;
    } catch (err) {
      await updateState(item.id, 'failed', {
        lastError: err instanceof Error ? err.message : String(err),
      });
    }
  }
  return processed;
}

/** Clear the ENTIRE queue (index + all blobs). Used on logout / failed biometric / remote wipe. */
export async function purgeQueue(): Promise<void> {
  const items = await readIndex();
  await Promise.all(items.map((i) => deleteBlob(i.id)));
  await removeConfidential(INDEX_KEY);
  if (isNative()) {
    try {
      const { Filesystem, Directory } = await import('@capacitor/filesystem');
      await Filesystem.rmdir({ path: BLOB_DIR, directory: Directory.Data, recursive: true });
    } catch {
      /* dir may not exist */
    }
  }
}

// ---------------------------------------------------------------------------
// Reachability
// ---------------------------------------------------------------------------

async function isOnline(): Promise<boolean> {
  if (isNative()) {
    try {
      const { Network } = await import('@capacitor/network');
      const status = await Network.getStatus();
      return status.connected;
    } catch {
      return true; // fail-open: never block a drain on a Network plugin error
    }
  }
  return typeof navigator === 'undefined' ? true : navigator.onLine;
}
